import SharedPreferences from "../../../constants/SharedPreferences";
import {
  saveInStorage,
  getFromStorage,
  getEncryptionCurveFromStorage,
} from "../../../util/Utility";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
} from "../../../crypto/AuthenticatedEncryption";
import Constants from "../../../constants/Constants";
import { encryptAsymmetric } from "../../../util/AsymmetricEncryption";
import { getLMSStatisticsKey } from "../../../manager/CryptManager";
import { saveMemberAppStatisticsKeysAPI } from "../statisticsApi";
import { encodeKey, decodeKey } from "../../../crypto/PBKDF2";
import { asRawKey } from "../../../crypto/KeyMaterial";

export async function decryptAndSaveMemberOrgSettingsFields(
  sessionId,
  orgName,
  data,
) {
  const orgKey = asRawKey(
    await getFromStorage(sessionId, SharedPreferences.ORG_KEY),
  );

  if (!orgKey) {
    return;
  }

  if (data.defaultEncryptionMode) {
    const defaultEncryptionMode = await aeadDecryptWithAssociatedData(
      data.defaultEncryptionMode,
      orgName,
      orgKey,
    );

    if (defaultEncryptionMode) {
      await saveInStorage(
        sessionId,
        SharedPreferences.DEFAULT_ENCRYPTION_MODE,
        defaultEncryptionMode,
      );
    }
  }

  if (data.defaultEncryptionPolicy) {
    const defaultEncryptionPolicy = await aeadDecryptWithAssociatedData(
      data.defaultEncryptionPolicy,
      orgName,
      orgKey,
    );

    if (defaultEncryptionPolicy) {
      await saveInStorage(
        sessionId,
        SharedPreferences.DEFAULT_ENCRYPTION_POLICY,
        defaultEncryptionPolicy,
      );
    }
  }

  if (data.defaultKeyGenerationPolicy) {
    const defaultKeyGenerationPolicy = await aeadDecryptWithAssociatedData(
      data.defaultKeyGenerationPolicy,
      orgName,
      orgKey,
    );

    if (defaultKeyGenerationPolicy) {
      await saveInStorage(
        sessionId,
        SharedPreferences.DEFAULT_KEY_GENERATION_POLICY,
        defaultKeyGenerationPolicy,
      );
    }
  }

  if (data.encryptionStatus) {
    const encryptionStatus = await aeadDecryptWithAssociatedData(
      data.encryptionStatus,
      orgName,
      orgKey,
    );

    if (encryptionStatus) {
      await saveInStorage(
        sessionId,
        SharedPreferences.IS_ENCRYPTION_ENABLED,
        encryptionStatus,
      );
    }
  }

  if (data.statsSyncTime) {
    const statsSyncTime = await aeadDecryptWithAssociatedData(
      data.statsSyncTime,
      orgName,
      orgKey,
    );

    if (statsSyncTime) {
      await saveInStorage(
        sessionId,
        SharedPreferences.STATS_SYNC_TIME_DURATION,
        statsSyncTime,
      );

      const currentTime = new Date().valueOf();
      const statsExpiryTime = currentTime + statsSyncTime * 60 * 1000;
      await saveInStorage(
        sessionId,
        SharedPreferences.STATS_SYNC_EXPIRY_TIME,
        statsExpiryTime,
      );
    }
  }
}

export async function decryptAndSaveMemberInfo(
  sessionId,
  orgName,
  memberClientDataResponse,
) {
  await decryptAndSaveMemberOrgSettingsFields(
    sessionId,
    orgName,
    memberClientDataResponse,
  );

  // decrypt and save statisticsKey (not dependent on orgKey)
  const orgMemberId = await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  );
  const appId = await getFromStorage(sessionId, SharedPreferences.APP_ID);

  let statisticsKey = null;

  if (memberClientDataResponse.lmsEncryptedStatisticsKey != null) {
    const lmsStatisticsKey = await getLMSStatisticsKey(
      await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
      await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
      orgMemberId,
      appId,
    );

    statisticsKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        memberClientDataResponse.lmsEncryptedStatisticsKey,
        Constants.LMS_KEY_PREFIX + orgName + orgMemberId + appId,
        lmsStatisticsKey,
      ),
    );
  } else if (memberClientDataResponse.statisticsKeys != null) {
    const lmsStatisticsKey = await getLMSStatisticsKey(
      await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
      await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
      orgMemberId,
      memberClientDataResponse.statisticsKeys[0].appId,
    );

    statisticsKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        memberClientDataResponse.statisticsKeys[0].lmsEncryptedStatisticsKey,
        Constants.LMS_KEY_PREFIX +
          orgName +
          orgMemberId +
          memberClientDataResponse.statisticsKeys[0].appId,
        lmsStatisticsKey,
      ),
    );
    const newLmsStatisticsKey = await getLMSStatisticsKey(
      await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
      await getFromStorage(sessionId, SharedPreferences.ORG_NAME),
      orgMemberId,
      appId,
    );

    const statisticsKeyEncoded = encodeKey(statisticsKey);
    const newLmsEncryptedStatisticsKey = await aeadEncryptWithAssociatedData(
      statisticsKeyEncoded,
      Constants.LMS_KEY_PREFIX + orgName + orgMemberId + appId,
      newLmsStatisticsKey,
    );

    const adminPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.ADMIN_PUBLIC_KEY,
    );

    const statisticsKeyEncryptionResponse = await encryptAsymmetric(
      statisticsKeyEncoded,
      adminPublicKey,
      orgName + orgMemberId + appId,
      await getEncryptionCurveFromStorage(sessionId),
    );
    let adminEncryptedStatisticsKey = "";
    let adminEncryptedStatisticsKeyKek = "";

    if (statisticsKeyEncryptionResponse) {
      adminEncryptedStatisticsKey =
        statisticsKeyEncryptionResponse.encryptedText;
      adminEncryptedStatisticsKeyKek =
        statisticsKeyEncryptionResponse.keyEncryptionKey;
    }

    const encryptionCount = await aeadEncryptWithAssociatedData(
      "0",
      orgName + orgMemberId + appId,
      statisticsKey,
    );

    const decryptionCount = await aeadEncryptWithAssociatedData(
      "0",
      orgName + orgMemberId + appId,
      statisticsKey,
    );

    const memberAppId = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
    );

    await saveMemberAppStatisticsKeysAPI(
      sessionId,
      decryptionCount,
      encryptionCount,
      adminEncryptedStatisticsKey,
      adminEncryptedStatisticsKeyKek,
      newLmsEncryptedStatisticsKey,
      memberAppId,
    );
  }

  if (statisticsKey) {
    await saveInStorage(
      sessionId,
      SharedPreferences.STATISTICS_KEY,
      encodeKey(statisticsKey),
    );
  }
}
